import { useMemo } from 'react';
import { useFetchData } from '../hooks/useFetchData';
import { v2ServicePath } from '../config';
import { normalizeV2ClassifierTrace } from './tracesApi';
import { formatSignalLabelFromKey, getSignalDescriptionFromKey } from '../utils/signalUtils';

const apiUrl = () => process.env.REACT_APP_API_URL || 'http://localhost:3001';

export function classifierLabel(key) {
  if (!key) return 'Classifier';
  // Names come from the contract via signalUtils. This used to hold its own
  // three-entry map, so every other signal fell through to a title-cased key and
  // workflow_anomaly still read "Workflow Anomaly" after it was renamed.
  return formatSignalLabelFromKey(key) || String(key);
}

const normalizeSubtypeDefinition = (subtype = {}) => ({
  ...subtype,
  key: subtype.key || '',
  name: subtype.name || formatClassifierValue(subtype.key),
  description: subtype.description || '',
  default_severity: subtype.default_severity || '',
  dynamic: Boolean(subtype.dynamic),
  event_keys: Array.isArray(subtype.event_keys) ? subtype.event_keys : [],
  event_key_patterns: Array.isArray(subtype.event_key_patterns) ? subtype.event_key_patterns : [],
});

export const buildClassifierDefinitionsIndex = (definitions = []) => {
  const byKey = new Map();
  (Array.isArray(definitions) ? definitions : []).forEach((definition) => {
    if (!definition?.classifier_key) return;
    byKey.set(definition.classifier_key, {
      ...definition,
      name: definition.name || classifierLabel(definition.classifier_key),
      description: definition.description || getSignalDescriptionFromKey(definition.classifier_key),
      subtypes: (Array.isArray(definition.subtypes) ? definition.subtypes : []).map(normalizeSubtypeDefinition),
      subtype_source_field: definition.subtype_source_field || 'primary_subtype',
    });
  });
  return byKey;
};

export const getClassifierDefinition = (definitionsIndex, classifierKey) => {
  if (!definitionsIndex || !classifierKey) return null;
  if (typeof definitionsIndex.get === 'function') return definitionsIndex.get(classifierKey) || null;
  return definitionsIndex[classifierKey] || null;
};

const formatPatternSuffix = (value) => String(value || '')
  .split('_')
  .filter(Boolean)
  .map(part => (/[A-Z]/.test(part.slice(1)) ? part : part.charAt(0).toUpperCase() + part.slice(1)))
  .join(' ');

const applyEventTemplate = (template, rawSuffix) => {
  const suffix = formatPatternSuffix(rawSuffix);
  if (!template) return suffix;
  return String(template).replace(/\{suffix\}/g, suffix).trim();
};

export const getClassifierEventDefinition = (subtypeDefinition, eventKey = '') => {
  if (!subtypeDefinition || !eventKey) return null;
  if (eventKey === subtypeDefinition.key) return null;

  const exact = (Array.isArray(subtypeDefinition.event_keys) ? subtypeDefinition.event_keys : [])
    .find(item => item?.key === eventKey);
  if (exact) {
    return {
      ...exact,
      name: exact.name || formatClassifierValue(exact.key),
      description: exact.description || '',
      dynamic: false,
    };
  }

  const pattern = (Array.isArray(subtypeDefinition.event_key_patterns) ? subtypeDefinition.event_key_patterns : [])
    .find(item => item?.key_prefix && eventKey.startsWith(item.key_prefix));
  if (!pattern) return null;

  const rawSuffix = eventKey.slice(String(pattern.key_prefix).length);
  return {
    key: eventKey,
    name: applyEventTemplate(pattern.name_template, rawSuffix) || formatClassifierValue(eventKey),
    description: applyEventTemplate(pattern.description_template, rawSuffix),
    default_severity: pattern.default_severity || subtypeDefinition.default_severity || '',
    dynamic: pattern.dynamic !== false,
    pattern,
  };
};

export const getClassifierSubtypeDefinition = (definition, detectionOrKey, eventKey = '') => {
  if (!definition) return null;
  const source = definition.subtype_source_field || 'primary_subtype';
  const key = typeof detectionOrKey === 'string'
    ? detectionOrKey
    : source === 'primary_event_key'
      ? detectionOrKey?.primary_event_key
      : detectionOrKey?.primary_subtype;
  const match = (Array.isArray(definition.subtypes) ? definition.subtypes : [])
    .find(subtype => subtype?.key === key);
  if (!match) return null;

  const rawEventKey = typeof detectionOrKey === 'string' ? eventKey : detectionOrKey?.primary_event_key;
  const eventDefinition = source !== 'primary_event_key'
    ? getClassifierEventDefinition(match, rawEventKey)
    : null;
  const typeName = match.name || classifierLabel(match.key);
  const eventName = eventDefinition?.name || (
    source !== 'primary_event_key' && rawEventKey && rawEventKey !== match.key
      ? formatClassifierValue(rawEventKey)
      : ''
  );
  return {
    ...match,
    type_key: match.key,
    type_name: typeName,
    event_key: rawEventKey || '',
    event_name: eventName,
    event_description: eventDefinition?.description || '',
    event_dynamic: Boolean(eventDefinition?.dynamic),
    event_definition: eventDefinition,
    display_name: eventName ? `${typeName} · ${eventName}` : (typeName || formatClassifierValue(match.key)),
    description: eventDefinition?.description || match.description || '',
    subtype_description: match.description || '',
  };
};

export const formatClassifierValue = (value) => {
  if (!value) return '';
  return String(value)
    .replace(/^http_(\d+)$/, 'HTTP $1')
    .replace(/^http_other$/, 'HTTP other')
    .replace(/^tool_args_/, 'Tool args ')
    .replace(/^tool_schema_/, 'Tool schema ')
    .replace(/^tool_/, 'Tool ')
    .replace(/^exception_/, 'Exception ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

export async function fetchClassifierDefinitions(selectedAppId, jwt, env, { signal } = {}) {
  if (!selectedAppId || !jwt) return [];
  const params = new URLSearchParams({ service_id: String(selectedAppId) });
  if (env) params.set('env', env);
  const response = await fetch(
    `${apiUrl()}/v2/data/classifiers/definitions?${params.toString()}`,
    {
      signal,
      headers: {
        Authorization: `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      },
    },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || `HTTP ${response.status}`);
  }
  const json = await response.json().catch(() => ({}));
  return Array.isArray(json?.data?.classifiers) ? json.data.classifiers : [];
}

export function useClassifierDefinitions(selectedAppId, { enabled = true } = {}) {
  const endpoint = selectedAppId ? '/v2/data/classifiers/definitions' : null;
  const params = useMemo(() => ({
    service_id: selectedAppId,
  }), [selectedAppId]);
  const { data, loading, error } = useFetchData({
    endpoint,
    params,
    enabled: Boolean(enabled && selectedAppId),
  });
  const definitions = Array.isArray(data?.data?.classifiers) ? data.data.classifiers : [];
  return {
    definitions,
    definitionsIndex: buildClassifierDefinitionsIndex(definitions),
    loading,
    error,
  };
}

const CLASSIFIER_KPI_GROUPS = ['positive', 'negative', 'agentic', 'safety'];

const emptyClassifierKpis = () => CLASSIFIER_KPI_GROUPS.reduce((acc, group) => {
  acc[group] = { total: 0, delta_1h: 0, delta_previous_period: 0 };
  return acc;
}, {});

const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const bucketTimeMs = (bucket) => {
  const time = new Date(bucket?.bucket_start || bucket?.bucket).getTime();
  return Number.isFinite(time) ? time : null;
};

const bucketDetectedCount = (bucket) => toFiniteNumber(bucket?.detected_count ?? bucket?.detected);

const recentDetectedCount = (buckets) => {
  const points = (Array.isArray(buckets) ? buckets : [])
    .map(bucket => ({
      timeMs: bucketTimeMs(bucket),
      detected: bucketDetectedCount(bucket),
    }))
    .filter(point => point.timeMs !== null);

  if (points.length === 0) return 0;

  const latestMs = Math.max(...points.map(point => point.timeMs));
  const oneHourMs = 60 * 60 * 1000;
  return points
    .filter(point => point.timeMs > latestMs - oneHourMs)
    .reduce((sum, point) => sum + point.detected, 0);
};

const previousPeriodDetectedDelta = (buckets) => {
  const points = (Array.isArray(buckets) ? buckets : [])
    .map(bucket => ({
      timeMs: bucketTimeMs(bucket),
      detected: bucketDetectedCount(bucket),
    }))
    .filter(point => point.timeMs !== null)
    .sort((a, b) => a.timeMs - b.timeMs);

  if (points.length === 0) return 0;

  const midpoint = Math.max(1, Math.floor(points.length / 2));
  const previous = points.slice(0, midpoint);
  const recent = points.slice(midpoint);
  const previousCount = previous.reduce((sum, point) => sum + point.detected, 0);
  const recentCount = recent.reduce((sum, point) => sum + point.detected, 0);

  return recentCount - previousCount;
};

const addPreviousPeriodDelta = (target, delta) => {
  if (delta == null) {
    target.delta_previous_period = null;
    return;
  }
  if (target.delta_previous_period == null) return;
  target.delta_previous_period += delta;
};

export function buildClassifierKpis(classifiers = []) {
  const kpis = emptyClassifierKpis();

  classifierSummariesToTrends(classifiers).forEach((classifier) => {
    const detected = toFiniteNumber(classifier?.detected_count);
    const buckets = classifier?.data;
    const recentDetected = recentDetectedCount(buckets);
    const previousPeriodDelta = classifier?.comparison_available === false
      ? null
      : classifier?.delta_detected_count != null
        ? toFiniteNumber(classifier.delta_detected_count)
        : previousPeriodDetectedDelta(buckets);
    const polarity = typeof classifier?.polarity === 'string'
      ? classifier.polarity.toLowerCase()
      : '';
    const tags = Array.isArray(classifier?.classifier_tags)
      ? classifier.classifier_tags.map(tag => `${tag}`.toLowerCase())
      : [];

    if (polarity === 'positive') {
      kpis.positive.total += detected;
      kpis.positive.delta_1h += recentDetected;
      addPreviousPeriodDelta(kpis.positive, previousPeriodDelta);
    }
    if (polarity === 'negative') {
      kpis.negative.total += detected;
      kpis.negative.delta_1h += recentDetected;
      addPreviousPeriodDelta(kpis.negative, previousPeriodDelta);
    }
    if (tags.includes('agentic')) {
      kpis.agentic.total += detected;
      kpis.agentic.delta_1h += recentDetected;
      addPreviousPeriodDelta(kpis.agentic, previousPeriodDelta);
    }
    if (tags.includes('safety')) {
      kpis.safety.total += detected;
      kpis.safety.delta_1h += recentDetected;
      addPreviousPeriodDelta(kpis.safety, previousPeriodDelta);
    }
  });

  return kpis;
}

export function classifierSummariesToTrends(classifiers = []) {
  const groups = new Map();

  (Array.isArray(classifiers) ? classifiers : [])
    .filter(item => item?.classifier_key)
    .forEach((item) => {
      const groupKey = `${item.classifier_key}:v${item.classifier_version || 1}`;
      const group = groups.get(groupKey) || {
        metric_name: item.classifier_key,
        display_name: item.name || classifierLabel(item.classifier_key),
        description: item.description || getSignalDescriptionFromKey(item.classifier_key),
        classifier_version: item.classifier_version || 1,
        polarity: typeof item.polarity === 'string' ? item.polarity.toLowerCase() : '',
        classifier_tags: new Set(),
        eligible_count: 0,
        detected_count: 0,
        previous_detected_count: 0,
        delta_detected_count: 0,
        hasPreviousDetectedCount: false,
        hasDeltaDetectedCount: false,
        comparison_available: undefined,
        severities: new Map(),
        buckets: new Map(),
      };

      const severity = item.severity || 'none';
      const detected = Number(item.detected_count || 0);
      group.eligible_count += Number(item.eligible_count || 0);
      group.detected_count += detected;
      if (item.previous_detected_count != null) {
        group.previous_detected_count += Number(item.previous_detected_count || 0);
        group.hasPreviousDetectedCount = true;
      }
      if (item.delta_detected_count != null) {
        group.delta_detected_count += Number(item.delta_detected_count || 0);
        group.hasDeltaDetectedCount = true;
      }
      if (item.comparison?.available === false) {
        group.comparison_available = false;
      } else if (item.comparison?.available === true && group.comparison_available !== false) {
        group.comparison_available = true;
      }
      if (!group.polarity && typeof item.polarity === 'string') {
        group.polarity = item.polarity.toLowerCase();
      }
      (Array.isArray(item.tags) ? item.tags : []).forEach((tag) => {
        const normalizedTag = `${tag || ''}`.trim();
        if (normalizedTag) group.classifier_tags.add(normalizedTag);
      });
      if (detected > 0) {
        group.severities.set(severity, (group.severities.get(severity) || 0) + detected);
      }

      (Array.isArray(item.buckets) ? item.buckets : []).forEach((bucket) => {
        const bucketKey = bucket.bucket_start;
        if (!bucketKey) return;
        const current = group.buckets.get(bucketKey) || { detected: 0, total: 0 };
        current.detected += Number(bucket.detected_count || 0);
        current.total += Number(bucket.eligible_count || bucket.trace_count || 0);
        group.buckets.set(bucketKey, current);
      });

      groups.set(groupKey, group);
    });

  return Array.from(groups.values()).map(group => ({
    metric_name: group.metric_name,
    display_name: group.display_name,
    description: group.description,
    classifier_version: group.classifier_version,
    polarity: group.polarity,
    classifier_tags: Array.from(group.classifier_tags),
    eligible_count: group.eligible_count,
    detected_count: group.detected_count,
    previous_detected_count: group.hasPreviousDetectedCount ? group.previous_detected_count : null,
    delta_detected_count: group.hasDeltaDetectedCount ? group.delta_detected_count : null,
    comparison_available: group.comparison_available,
    tags: Array.from(group.severities.entries()).map(([severity, count]) => (
      `${severity} ${Number(count).toLocaleString()}`
    )),
    data: Array.from(group.buckets.entries()).map(([bucket, values]) => ({
      bucket,
      detected: values.detected,
      total: values.total,
    })),
  }));
}

export function useClassifierOverview(selectedAppId, { enabled = true, limit = 50, includeTimeseries = false } = {}) {
  const endpoint = selectedAppId
    ? v2ServicePath(selectedAppId, '/classifiers')
    : null;
  const params = useMemo(() => ({
    limit,
    ...(includeTimeseries ? { include_timeseries: 'true' } : {}),
    include_metadata: 'true',
  }), [limit, includeTimeseries]);
  const { data, loading, error } = useFetchData({
    endpoint,
    params,
    enabled: Boolean(enabled && endpoint),
  });
  const classifiers = Array.isArray(data?.data) ? data.data : [];
  return {
    classifiers,
    classifierTrends: classifierSummariesToTrends(classifiers),
    bucketSize: data?.meta?.bucket || null,
    loading,
    error,
  };
}

export function buildClassifierTraceMatchParams({
  status = 'detected', limit = 50, subtype = '', eventKey = '',
  workflow = '', workflowId = '', problemId = '',
} = {}) {
  const next = { status, limit };
  if (subtype) next.subtype = subtype;
  if (eventKey) next.event_key = eventKey;
  if (workflowId) next.workflow_id = workflowId;
  else if (workflow) next.workflow = workflow;
  if (problemId) next.problem_id = problemId;
  return next;
}

export function useClassifierTraceMatches(
  selectedAppId,
  classifierKey,
  {
    enabled = true, status = 'detected', limit = 50, subtype = '', eventKey = '',
    workflow = '', workflowId = '', problemId = '',
  } = {},
) {
  const endpoint = selectedAppId && classifierKey
    ? v2ServicePath(selectedAppId, `/classifiers/${encodeURIComponent(classifierKey)}/traces`)
    : null;
  const params = useMemo(() => buildClassifierTraceMatchParams({
    status,
    limit,
    subtype,
    eventKey,
    workflow,
    workflowId,
    problemId,
  }), [status, limit, subtype, eventKey, workflow, workflowId, problemId]);
  const { data, loading, error } = useFetchData({
    endpoint,
    params,
    enabled: Boolean(enabled && endpoint),
  });

  return {
    traces: Array.isArray(data?.data) ? data.data.map(normalizeV2ClassifierTrace) : [],
    loading,
    error,
    hasMore: Boolean(data?.meta?.has_more),
    meta: data?.meta || null,
  };
}

export function useSignalInvestigation(
  selectedAppId,
  classifierKey,
  {
    enabled = true,
    limitGroups = 10,
    limitBreakdowns = 5,
    limitSamples = 3,
  } = {},
) {
  const endpoint = selectedAppId && classifierKey
    ? v2ServicePath(selectedAppId, `/classifiers/${encodeURIComponent(classifierKey)}/investigation`)
    : null;
  const params = useMemo(() => ({
    limit: limitGroups,
    limit_breakdowns: limitBreakdowns,
    limit_samples: limitSamples,
  }), [limitGroups, limitBreakdowns, limitSamples]);
  const { data, loading, error } = useFetchData({
    endpoint,
    params,
    enabled: Boolean(enabled && endpoint),
  });

  return {
    investigation: data?.data || null,
    loading,
    error,
    meta: data?.meta || null,
  };
}

async function fetchClassifierEndpoint(
  selectedAppId,
  classifierKey,
  jwt,
  subPath,
  {
    signal,
    env = '',
    range = '',
    status = 'detected',
    limit = 500,
    subtype = '',
    eventKey = '',
    event_key = '',
    problemId = '',
    problem_id = '',
  } = {},
) {
  if (!selectedAppId || !classifierKey || !jwt) return null;

  const params = new URLSearchParams({
    status,
    limit: String(limit),
  });
  if (env) params.set('env', env);
  if (range) params.set('range', range);
  if (subtype) params.set('subtype', subtype);
  if (eventKey || event_key) params.set('event_key', eventKey || event_key);
  if (problemId || problem_id) {
    params.set('problem_id', problemId || problem_id);
  }

  const response = await fetch(
    `${apiUrl()}${v2ServicePath(selectedAppId, `/classifiers/${encodeURIComponent(classifierKey)}${subPath}`)}?${params.toString()}`,
    {
      signal,
      headers: {
        Authorization: `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      },
    },
  );

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || `HTTP ${response.status}`);
  }

  return response.json();
}

export async function fetchClassifierDimensions(
  selectedAppId,
  classifierKey,
  jwt,
  options = {},
) {
  const response = await fetchClassifierEndpoint(selectedAppId, classifierKey, jwt, '/dimensions', options);
  return response?.data || null;
}

export async function fetchClassifierTraceRows(
  selectedAppId,
  classifierKey,
  jwt,
  options = {},
) {
  const response = await fetchClassifierTraceRowsWithMeta(selectedAppId, classifierKey, jwt, options);
  return response.rows;
}

export async function fetchClassifierTraceRowsWithMeta(
  selectedAppId,
  classifierKey,
  jwt,
  options = {},
) {
  const response = await fetchClassifierEndpoint(selectedAppId, classifierKey, jwt, '/traces', options);
  return {
    rows: Array.isArray(response?.data) ? response.data : [],
    meta: response?.meta || {},
  };
}

// ---------------------------------------------------------------------------
// Classifier (signal) write endpoints — v2, service-scoped.
// service_id comes from the path (v2ServicePath); environment from ?env=.
// Every function throws Error(message) on a non-2xx response so callers can
// surface the failure instead of swallowing it.
// ---------------------------------------------------------------------------

async function classifierMutate(selectedAppId, subPath, jwt, env, { method, body } = {}) {
  const query = env ? `?env=${encodeURIComponent(env)}` : '';
  const response = await fetch(
    `${apiUrl()}${v2ServicePath(selectedAppId, subPath)}${query}`,
    {
      method,
      headers: {
        Authorization: `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    },
  );
  if (!response.ok) {
    const errBody = await response.json().catch(() => ({}));
    throw new Error(errBody?.error || `HTTP ${response.status}`);
  }
  const json = await response.json().catch(() => ({}));
  return json?.data ?? json;
}

export function createClassifier(selectedAppId, jwt, env, payload) {
  return classifierMutate(selectedAppId, '/classifiers/definitions', jwt, env, { method: 'POST', body: payload });
}

export function updateClassifier(selectedAppId, jwt, env, classifierId, payload) {
  return classifierMutate(
    selectedAppId, `/classifiers/definitions/${encodeURIComponent(classifierId)}`, jwt, env,
    { method: 'PUT', body: payload },
  );
}

export function deleteClassifier(selectedAppId, jwt, env, classifierId) {
  return classifierMutate(
    selectedAppId, `/classifiers/definitions/${encodeURIComponent(classifierId)}`, jwt, env,
    { method: 'DELETE' },
  );
}

export function setClassifierStatus(selectedAppId, jwt, env, classifierId, action) {
  return classifierMutate(
    selectedAppId, `/classifiers/definitions/${encodeURIComponent(classifierId)}/${action}`, jwt, env,
    { method: 'POST' },
  );
}

export function createClassifierLabel(selectedAppId, jwt, env, payload) {
  return classifierMutate(selectedAppId, '/classifiers/labels', jwt, env, { method: 'POST', body: payload });
}

export function deleteClassifierLabel(selectedAppId, jwt, env, labelId) {
  return classifierMutate(
    selectedAppId, `/classifiers/labels/${encodeURIComponent(labelId)}`, jwt, env, { method: 'DELETE' },
  );
}

export async function fetchClassifierLabels(selectedAppId, jwt, env, { classifierType, limit = 20, offset = 0 } = {}) {
  const params = new URLSearchParams({
    classifier_type: classifierType,
    limit: String(limit),
    offset: String(offset),
  });
  if (env) params.set('env', env);
  const response = await fetch(
    `${apiUrl()}${v2ServicePath(selectedAppId, '/classifiers/labels')}?${params.toString()}`,
    { headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' } },
  );
  if (!response.ok) {
    const errBody = await response.json().catch(() => ({}));
    throw new Error(errBody?.error || `HTTP ${response.status}`);
  }
  const json = await response.json().catch(() => ({}));
  return json?.data ?? json;
}
